import React from "react"
import styled from "styled-components"
import { animationProperties } from "../../../utils"
import { AosWrapper } from "../../molecules/AosWrapper"
import useDeviceTheme from "@project/shared/src/useDeviceTheme"

interface CardProps {
  icon: string
  lightIcon: string
  title: string
  description: string
  index: number
}

const OuterDiv = styled.div`
  position: relative;
  z-index: 2;
  max-width: 666px;

  .linear-gradient {
    position: absolute;
    width: 4px;
    height: 85px;
    top: calc(50% - 85px / 2 - 0.5px);
    border-radius: 100%;
    background: ${(props) => props.theme.highlightGradient};
    @media (max-width: 633px) {
      height: 50px;
      top: calc(50% - 50px / 2 - 0.5px);
    }
  }
  .odd {
    right: -3px !important;
  }
  .even {
    left: -3px;
  }
  .left {
    left: -73px;
    width: 110px;
    height: 190px;
    position: absolute;
    border: 10px dashed ${(props) => props.theme.footerSectionColor};
    border-right: none;
    top: 45%;
    @media (max-width: 768px) {
      left: -30px;
      height: 215px;
    }
    @media (max-width: 633px) {
      height: 210px;
      top: 47%;
    }
    @media (max-width: 394px) {
      display: none;
    }
  }
  .left::before {
    content: "";
    position: absolute;
    top: -2px;
    right: -2px;
    bottom: -2px;
    left: -2px;
    border: 2px dashed transparent;
    z-index: -1;
    border-image: ${(props) => props.theme.serviceCardLeftLinearGradient};
  }
  .right {
    right: -73px;
    width: 110px;
    height: 190px;
    position: absolute;
    border: 10px dashed ${(props) => props.theme.footerSectionColor};
    border-left: none;
    top: 45%;
    @media (max-width: 768px) {
      right: -30px;
      height: 215px;
    }
    @media (max-width: 633px) {
      height: 210px;
      top: 47%;
    }
    @media (max-width: 394px) {
      display: none;
    }
  }
  .right::before {
    content: "";
    position: absolute;
    top: -2px;
    right: -2px;
    bottom: -2px;
    left: -2px;
    border: 2px dashed transparent;
    z-index: -1;
    border-image: ${(props) => props.theme.serviceCardRightLinearGradient};
  }
  .card-title {
    padding-bottom: 10px;
    font-size: 18px;
    font-weight: 600;
    line-height: 32px;
    @media (max-width: 1023px) {
      font-size: 16px;
    }
    @media (max-width: 600px) {
      line-height: 24px;
    }
  }
`

const Card = styled.div<{ isLightTheme: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 20px;
  gap: 40px;
  isolation: isolate;
  position: relative;
  box-shadow: ${(props) => props.theme.shadowedBox};
  ${(props) => props.isLightTheme && `border: 1px solid #ddd;`};
  z-index: 2;

  max-width: 741px;
  min-height: 138px;
  width: 100%;

  background: ${(props) => props.theme.historyCardGradient};
  border-radius: 20px;
  @media (max-width: 1023px) {
    gap: 20px;
    padding: 16px;
  }
  @media (max-width: 600px) {
    display: flex;
    max-width: 335px;
    align-items: flex-start;
    width: 100%;
    gap: 16px;
    padding: 20px;
  }

  img {
    height: 70px;
    width: 70px;
    @media (max-width: 1023px) {
      padding-top: 0px;
      height: 50px;
      width: 50px;
    }
    @media (max-width: 600px) {
      padding-top: 0px;
      height: 40px;
      width: 40px;
    }
  }
`

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  h3 {
    max-width: 531px;
    min-height: 33px;

    ${(props) => props.theme.typography.cardTitle}
    /* identical to box height, or 33px */

    display: flex;
    align-items: center;

    /* White */

    color: ${(props) => props.theme.fontColor};
    @media (max-width: 768px) {
      font-size: 1rem;
    }
    @media (max-width: 633px) {
      font-size: 1rem;
      width: 100%;
      text-align: start;
      display: flex;
      justify-content: flex-start;
    }
  }
  p {
    max-width: 531px;
    min-height: 58px;
    font-size: 0.875rem;
    line-height: 24px;
    ${(props) => props.theme.typography.fontColor}

    color: ${(props) => props.theme.whiteAlpha60};
    @media (max-width: 768px) {
      font-size: 0.875rem;
    }
    @media (max-width: 633px) {
      font-size: 0.875rem;
      width: 100%;
      text-align: start;
      display: flex;
      justify-content: flex-start;
    }
  }
`

const BusinessTechCardComponent: React.FC<CardProps> = ({
  icon,
  lightIcon,
  title,
  description,
  index,
}) => {
  const isDarkTheme = useDeviceTheme()

  const gradientPosition = (index) => {
    if (index % 2 != 0) {
      return <div className={"linear-gradient even"}></div>
    }
    return <div className={"linear-gradient odd"}></div>
  }
  const cardPosition = (index) => {
    if (index == 3) {
      return <div />
    }

    if (index % 2 != 0) {
      return <div className={"left"} />
    }

    return <div className={"right"} />
  }
  return (
    <OuterDiv style={{ position: "relative", display: "flex" }}>
      <AosWrapper
        duration={animationProperties.duration + 0.5}
        delay={animationProperties.delay + 0.5}
        animateIn={"animate__fadeInUpBig animate__zoomIn"}
      >
        <Card isLightTheme={!isDarkTheme}>
          <img src={isDarkTheme ? icon : lightIcon} />
          <CardContent>
            <h3 className="card-title">{title}</h3>
            <p>{description} </p>
          </CardContent>
        </Card>
        {cardPosition(index)}
        {gradientPosition(index)}
      </AosWrapper>
    </OuterDiv>
  )
}

export { BusinessTechCardComponent }
